import React from 'react';
import { notifications, extractApiErrorMessage, colors } from '../../utils';
import { Title, Loader, ClearFilters } from '../../elements';
import {
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../theme/v4Compat';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { JSONTree } from 'react-json-tree';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import { HealthSystemDomainHealthInjectorsDtosAppEventLogDto } from '../../api/HealthSystemApi';

interface HealthInjectorEventsTableProps {
  injectorId: string;
}

const HealthInjectorEventsTable: React.FC<HealthInjectorEventsTableProps> = ({ injectorId }) => {
  const styles = sxStyles(useTheme());
  const { t } = useTranslation();
  const apiService = new ApiService();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [errorOnly, setErrorOnly] = React.useState(false);
  const [fromDate, setFromDate] = React.useState<Dayjs | null>(null);
  const [toDate, setToDate] = React.useState<Dayjs | null>(null);
  const [events, setEvents] = React.useState<HealthSystemDomainHealthInjectorsDtosAppEventLogDto[]>([]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setEvents([]);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };

  const loadEvents = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    apiService.api.device
      .deviceGetHealthInjectorEvents({
        id: injectorId,
        page: searchPage,
        size: rowsPerPage,
        isError: errorOnly || undefined,
        fromDate: fromDate ? fromDate.toISOString() : undefined,
        toDate: toDate ? toDate.toISOString() : undefined
      })
      .then((result) => {
        setEvents(result.data.events);
        setTotal(result.data.totalCount);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadEvents();
      loadData();
    }
  }, [loaded]);

  const clearFilters = () => {
    setErrorOnly(false);
    setFromDate(null);
    setToDate(null);
    setPage(0);
  };

  const handleErrorsOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrorOnly(event.target.checked);
    setPage(0);
  };

  function EventLogTableRow(props: { eventLog: HealthSystemDomainHealthInjectorsDtosAppEventLogDto }) {
    const { eventLog: log } = props;
    const [rowExpanded, setRowExpanded] = React.useState(false);

    const renderAdditionalInfo = () => {
      try {
        const additionalInfo = JSON.parse(log.payload?.additionalInfo || '{}');
        return <JSONTree data={additionalInfo} theme={colors.jsonViewTheme} />;
      } catch {
        return <Typography variant="body2">{log.payload?.additionalInfo}</Typography>;
      }
    };

    return (
      <React.Fragment key={log.id}>
        <TableRow key={`btn${log.id}`} hover selected={rowExpanded}>
          <TableCell align="center">
            <Tooltip arrow title={rowExpanded ? t('f-logs-hide') : t('f-logs-expand')}>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setRowExpanded(!rowExpanded)}
                sx={V4_ICON_BUTTON_SMALL}
              >
                {rowExpanded ? <Icon>keyboard_arrow_up</Icon> : <Icon>keyboard_arrow_down</Icon>}
              </IconButton>
            </Tooltip>
          </TableCell>
          <TableCell>{new Date(log.dateTime).toLocaleString()}</TableCell>
          <TableCell>{log.applicationType}</TableCell>
          <TableCell>{log.applicationVersion}</TableCell>
          <TableCell>{log.user?.user?.name || '-'}</TableCell>
          <TableCell>{log.payload?.action}</TableCell>
          <TableCell>
            {log.payload?.isError && (
              <Tooltip arrow title={t('f-logs-error')}>
                <Icon color="error">error_outline</Icon>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>

        <TableRow key={`row${log.id}`}>
          <TableCell sx={styles.tableRowArrowCell} colSpan={7}>
            <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
              <Grid container sx={styles.breakWord} spacing={2}>
                <Grid item lg={4} md={6} sm={12}>
                  <Typography variant="h6">{t('f-user')}</Typography>
                  <Divider />
                  <Typography variant="subtitle2">{t('f-usr-name')}:</Typography>
                  <Typography variant="body1">{log.user?.user?.name || '-'}</Typography>
                  <Typography variant="subtitle2">{t('f-usr-role')}:</Typography>
                  <Typography variant="body1">{log.user?.user?.role || '-'}</Typography>
                  <Typography variant="subtitle2">{t('f-logs-ip')}:</Typography>
                  <Typography variant="body1">{log.user?.requestIpAddress || '-'}</Typography>
                </Grid>

                {log.user?.injector && (
                  <Grid item lg={4} md={6} sm={12}>
                    <Typography variant="h6">{t('f-settings-injector')}</Typography>
                    <Divider />
                    <Typography variant="subtitle2">{t('f-logs-mac')}:</Typography>
                    <Typography variant="body1">{log.user?.injector?.macAddress || '-'}</Typography>
                    <Typography variant="subtitle2">{t('m-firmware-controller-application')}:</Typography>
                    <Typography variant="body1">{log.user?.injector?.application || '-'}</Typography>
                    <Typography variant="subtitle2">{t('m-firmware-controller-bootloader')}:</Typography>
                    <Typography variant="body1">{log.user?.injector?.bootloader || '-'}</Typography>
                  </Grid>
                )}

                <Grid item lg={4} md={6} sm={12}>
                  <Typography variant="h6">{t('f-logs-payload')}</Typography>
                  <Divider />
                  <Typography variant="subtitle2">{t('f-com-action')}:</Typography>
                  <Typography variant="body1">{log.payload?.action || '-'}</Typography>
                  <Typography variant="subtitle2">{t('f-logs-msg')}:</Typography>
                  <Typography variant="body1" sx={{ display: 'block' }}>
                    {log.payload?.message || '-'}
                  </Typography>
                  <Typography variant="subtitle2">{t('f-logs-stack')}:</Typography>
                  <Typography variant="body2">{log.payload?.stackTrace || '-'}</Typography>
                  <Typography variant="subtitle2">{t('f-logs-add-info')}:</Typography>
                  {renderAdditionalInfo()}
                </Grid>
              </Grid>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item container xs={12} spacing={1}>
        <Grid item>
          <Icon color="primary" fontSize="large">
            receipt_long
          </Icon>
        </Grid>
        <Grid item>
          <Title>{t('f-logs-events')}</Title>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid item container md={12} spacing={1} sx={styles.filtersRow}>
              <Grid item md={2} xs={6}>
                <DatePicker
                  label={t('f-com-from-date')}
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue as Dayjs | null)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid item md={2} xs={6}>
                <DatePicker
                  label={t('f-com-to-date')}
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue as Dayjs | null)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid item>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={errorOnly} onChange={handleErrorsOnlyChange} />}
                  label={t('f-logs-error-only')}
                />
              </Grid>

              <Grid item>
                <ClearFilters onClick={clearFilters} />
              </Grid>

              <Grid item>
                <Button
                  id="searchBtn"
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={styles.formControl}
                  onClick={() => setLoaded(false)}
                  startIcon={<Icon>search</Icon>}
                >
                  {t('f-search')}
                </Button>
              </Grid>
            </Grid>
          </LocalizationProvider>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && (
            <Grid item md={12}>
              <Paper elevation={1}>
                <Table size="small" sx={V4_TABLE}>
                  <TableHead sx={styles.tableHeader}>
                    <TableRow>
                      <TableCell>{''}</TableCell>
                      <TableCell>{t('f-com-date-time')}</TableCell>
                      <TableCell>{t('f-com-apptype')}</TableCell>
                      <TableCell>{t('f-device-ver')}</TableCell>
                      <TableCell>{t('f-user')}</TableCell>
                      <TableCell>{t('f-com-action')}</TableCell>
                      <TableCell>{''}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {events.map((log, index) => (
                      <EventLogTableRow key={index} eventLog={log} />
                    ))}
                  </TableBody>

                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default HealthInjectorEventsTable;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    tableHeader: {
      background: colors.defaultGrey
    },
    paperHeader: {
      justifyContent: 'space-between'
    },
    justifyRight: {
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    centred: {
      alignItems: 'center'
    },
    withMargin: {
      margin: theme.spacing(1)
    },
    marginTop2: {
      marginTop: theme.spacing(2)
    },
    filtersRow: {
      alignItems: 'center'
    },
    formControl: {
      minWidth: 150
    },
    tableRowArrowCell: {
      paddingBottom: 0,
      paddingTop: 0
    },
    breakWord: {
      wordBreak: 'break-word'
    }
  }) as const;
